"use client";

import * as React from "react";

/**
 * Internationalization (i18n) utilities for the design system.
 * Provides RTL support, number/date formatting, and text direction detection.
 */

// =============================================================================
// LOCALE CONTEXT
// =============================================================================

export interface LocaleConfig {
  /** BCP 47 language tag (e.g., "en-US", "ar-SA") */
  locale: string;
  /** Text direction */
  dir: "ltr" | "rtl";
  /** Timezone for date formatting */
  timezone?: string;
  /** Custom number formatting options */
  numberFormat?: Intl.NumberFormatOptions;
  /** Custom date formatting options */
  dateFormat?: Intl.DateTimeFormatOptions;
}

const defaultLocale: LocaleConfig = {
  locale: "en-US",
  dir: "ltr",
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
};

const LocaleContext = React.createContext<LocaleConfig>(defaultLocale);

export function useLocale(): LocaleConfig {
  return React.useContext(LocaleContext);
}

export interface LocaleProviderProps {
  children: React.ReactNode;
  locale?: string;
  dir?: "ltr" | "rtl";
  timezone?: string;
}

/**
 * Provides locale configuration to the component tree.
 *
 * @example
 * <LocaleProvider locale="ar-SA" dir="rtl">
 *   <App />
 * </LocaleProvider>
 */
export function LocaleProvider({
  children,
  locale = "en-US",
  dir,
  timezone,
}: LocaleProviderProps) {
  const detectedDir = dir ?? detectTextDirection(locale);

  const value = React.useMemo<LocaleConfig>(
    () => ({
      locale,
      dir: detectedDir,
      timezone: timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
    }),
    [locale, detectedDir, timezone]
  );

  return (
    <LocaleContext.Provider value={value}>
      <div dir={detectedDir} lang={locale}>
        {children}
      </div>
    </LocaleContext.Provider>
  );
}

// =============================================================================
// RTL SUPPORT
// =============================================================================

/** RTL language codes */
const RTL_LANGUAGES = new Set([
  "ar", // Arabic
  "he", // Hebrew
  "fa", // Persian/Farsi
  "ur", // Urdu
  "ps", // Pashto
  "sd", // Sindhi
  "yi", // Yiddish
  "ku", // Kurdish (some variants)
  "dv", // Divehi
  "ug", // Uyghur
]);

/**
 * Detect text direction based on locale.
 *
 * @example
 * detectTextDirection("ar-SA"); // "rtl"
 * detectTextDirection("en-US"); // "ltr"
 */
export function detectTextDirection(locale: string): "ltr" | "rtl" {
  const lang = locale.split("-")[0].toLowerCase();
  return RTL_LANGUAGES.has(lang) ? "rtl" : "ltr";
}

/**
 * Hook to check if current locale is RTL.
 *
 * @example
 * const isRtl = useIsRtl();
 * // Use for conditional styling or icon direction
 */
export function useIsRtl(): boolean {
  const { dir } = useLocale();
  return dir === "rtl";
}

/**
 * Get logical CSS property name based on direction.
 * Helps convert physical properties to logical ones.
 *
 * @example
 * getLogicalProperty("left", "rtl"); // "right"
 * getLogicalProperty("marginLeft", "rtl"); // "marginRight"
 */
export function getLogicalProperty(
  property: string,
  dir: "ltr" | "rtl"
): string {
  if (dir === "ltr") return property;

  const mappings: Record<string, string> = {
    left: "right",
    right: "left",
    marginLeft: "marginRight",
    marginRight: "marginLeft",
    paddingLeft: "paddingRight",
    paddingRight: "paddingLeft",
    borderLeft: "borderRight",
    borderRight: "borderLeft",
    textAlign: "textAlign", // handled separately
  };

  return mappings[property] ?? property;
}

// =============================================================================
// NUMBER FORMATTING
// =============================================================================

/**
 * Format a number according to locale.
 *
 * @example
 * formatNumber(1234567.89, "en-US"); // "1,234,567.89"
 * formatNumber(1234567.89, "de-DE"); // "1.234.567,89"
 * formatNumber(1234567.89, "ar-SA"); // "١٬٢٣٤٬٥٦٧٫٨٩"
 */
export function formatNumber(
  value: number,
  locale: string,
  options?: Intl.NumberFormatOptions
): string {
  return new Intl.NumberFormat(locale, options).format(value);
}

/**
 * Format a number as currency.
 *
 * @example
 * formatCurrency(99.99, "en-US", "USD"); // "$99.99"
 * formatCurrency(99.99, "de-DE", "EUR"); // "99,99 €"
 * formatCurrency(99.99, "ja-JP", "JPY"); // "￥100"
 */
export function formatCurrency(
  value: number,
  locale: string,
  currency: string,
  options?: Omit<Intl.NumberFormatOptions, "style" | "currency">
): string {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    ...options,
  }).format(value);
}

/**
 * Format a number as a percentage.
 *
 * @example
 * formatPercent(0.1234, "en-US"); // "12%"
 * formatPercent(0.1234, "en-US", { maximumFractionDigits: 1 }); // "12.3%"
 */
export function formatPercent(
  value: number,
  locale: string,
  options?: Omit<Intl.NumberFormatOptions, "style">
): string {
  return new Intl.NumberFormat(locale, {
    style: "percent",
    maximumFractionDigits: 0,
    ...options,
  }).format(value);
}

/**
 * Format a number in compact notation.
 *
 * @example
 * formatCompact(1234, "en-US"); // "1.2K"
 * formatCompact(1234567, "en-US"); // "1.2M"
 */
export function formatCompact(
  value: number,
  locale: string,
  options?: Omit<Intl.NumberFormatOptions, "notation">
): string {
  return new Intl.NumberFormat(locale, {
    notation: "compact",
    maximumFractionDigits: 1,
    ...options,
  }).format(value);
}

/**
 * Hook for number formatting with current locale.
 */
export function useNumberFormat(options?: Intl.NumberFormatOptions) {
  const { locale } = useLocale();

  return React.useMemo(
    () => ({
      format: (value: number) => formatNumber(value, locale, options),
      formatCurrency: (value: number, currency: string) =>
        formatCurrency(value, locale, currency, options),
      formatPercent: (value: number) => formatPercent(value, locale, options),
      formatCompact: (value: number) => formatCompact(value, locale, options),
    }),
    [locale, options]
  );
}

// =============================================================================
// DATE & TIME FORMATTING
// =============================================================================

/**
 * Format a date according to locale.
 *
 * @example
 * formatDate(new Date(), "en-US"); // "1/15/2024"
 * formatDate(new Date(), "de-DE"); // "15.1.2024"
 * formatDate(new Date(), "ja-JP"); // "2024/1/15"
 */
export function formatDate(
  date: Date | number | string,
  locale: string,
  options?: Intl.DateTimeFormatOptions
): string {
  const d = typeof date === "string" ? new Date(date) : date;
  return new Intl.DateTimeFormat(locale, options).format(d);
}

/**
 * Format a date with time.
 *
 * @example
 * formatDateTime(new Date(), "en-US"); // "1/15/2024, 3:30 PM"
 */
export function formatDateTime(
  date: Date | number | string,
  locale: string,
  options?: Intl.DateTimeFormatOptions
): string {
  const d = typeof date === "string" ? new Date(date) : date;
  return new Intl.DateTimeFormat(locale, {
    dateStyle: "short",
    timeStyle: "short",
    ...options,
  }).format(d);
}

/**
 * Format time only.
 *
 * @example
 * formatTime(new Date(), "en-US"); // "3:30 PM"
 * formatTime(new Date(), "de-DE"); // "15:30"
 */
export function formatTime(
  date: Date | number | string,
  locale: string,
  options?: Intl.DateTimeFormatOptions
): string {
  const d = typeof date === "string" ? new Date(date) : date;
  return new Intl.DateTimeFormat(locale, {
    timeStyle: "short",
    ...options,
  }).format(d);
}

/**
 * Format a relative time (e.g., "2 hours ago", "in 3 days").
 *
 * @example
 * formatRelativeTime(-2, "hours", "en-US"); // "2 hours ago"
 * formatRelativeTime(3, "days", "en-US"); // "in 3 days"
 */
export function formatRelativeTime(
  value: number,
  unit: Intl.RelativeTimeFormatUnit,
  locale: string,
  options?: Intl.RelativeTimeFormatOptions
): string {
  return new Intl.RelativeTimeFormat(locale, {
    numeric: "auto",
    ...options,
  }).format(value, unit);
}

/**
 * Get relative time from a date (auto-selects appropriate unit).
 *
 * @example
 * getRelativeTime(new Date(Date.now() - 3600000), "en-US"); // "1 hour ago"
 */
export function getRelativeTime(
  date: Date | number | string,
  locale: string
): string {
  const d = typeof date === "string" ? new Date(date) : new Date(date);
  const now = Date.now();
  const diff = d.getTime() - now;
  const absDiff = Math.abs(diff);

  const units: Array<{ unit: Intl.RelativeTimeFormatUnit; ms: number }> = [
    { unit: "year", ms: 365 * 24 * 60 * 60 * 1000 },
    { unit: "month", ms: 30 * 24 * 60 * 60 * 1000 },
    { unit: "week", ms: 7 * 24 * 60 * 60 * 1000 },
    { unit: "day", ms: 24 * 60 * 60 * 1000 },
    { unit: "hour", ms: 60 * 60 * 1000 },
    { unit: "minute", ms: 60 * 1000 },
    { unit: "second", ms: 1000 },
  ];

  for (const { unit, ms } of units) {
    if (absDiff >= ms) {
      const value = Math.round(diff / ms);
      return formatRelativeTime(value, unit, locale);
    }
  }

  return formatRelativeTime(0, "second", locale);
}

/**
 * Hook for date formatting with current locale.
 */
export function useDateFormat(options?: Intl.DateTimeFormatOptions) {
  const { locale, timezone } = useLocale();

  const opts = React.useMemo(
    () => ({ timeZone: timezone, ...options }),
    [timezone, options]
  );

  return React.useMemo(
    () => ({
      format: (date: Date | number | string) => formatDate(date, locale, opts),
      formatDateTime: (date: Date | number | string) =>
        formatDateTime(date, locale, opts),
      formatTime: (date: Date | number | string) =>
        formatTime(date, locale, opts),
      formatRelative: (date: Date | number | string) =>
        getRelativeTime(date, locale),
    }),
    [locale, opts]
  );
}

// =============================================================================
// LIST FORMATTING
// =============================================================================

/**
 * Format a list of items with proper conjunctions.
 *
 * @example
 * formatList(["apples", "oranges", "bananas"], "en-US"); // "apples, oranges, and bananas"
 * formatList(["apples", "oranges", "bananas"], "en-US", { style: "short" }); // "apples, oranges, & bananas"
 */
export function formatList(
  items: string[],
  locale: string,
  options?: Intl.ListFormatOptions
): string {
  return new Intl.ListFormat(locale, {
    type: "conjunction",
    style: "long",
    ...options,
  }).format(items);
}

// =============================================================================
// PLURALIZATION
// =============================================================================

/**
 * Get plural category for a number.
 *
 * @example
 * getPluralCategory(0, "en-US"); // "other"
 * getPluralCategory(1, "en-US"); // "one"
 * getPluralCategory(2, "en-US"); // "other"
 */
export function getPluralCategory(
  count: number,
  locale: string,
  type: "cardinal" | "ordinal" = "cardinal"
): Intl.LDMLPluralRule {
  return new Intl.PluralRules(locale, { type }).select(count);
}

/**
 * Simple pluralization helper.
 *
 * @example
 * pluralize(0, { one: "item", other: "items" }, "en-US"); // "items"
 * pluralize(1, { one: "item", other: "items" }, "en-US"); // "item"
 * pluralize(5, { one: "item", other: "items" }, "en-US"); // "items"
 */
export function pluralize(
  count: number,
  forms: Partial<Record<Intl.LDMLPluralRule, string>>,
  locale: string
): string {
  const category = getPluralCategory(count, locale);
  return forms[category] ?? forms.other ?? "";
}

/**
 * Hook for pluralization with current locale.
 */
export function usePluralize() {
  const { locale } = useLocale();

  return React.useCallback(
    (count: number, forms: Partial<Record<Intl.LDMLPluralRule, string>>) =>
      pluralize(count, forms, locale),
    [locale]
  );
}

// =============================================================================
// DISPLAY NAMES
// =============================================================================

/**
 * Get localized display name for a language, region, or currency.
 *
 * @example
 * getDisplayName("en", "language", "de-DE"); // "Englisch"
 * getDisplayName("US", "region", "ja-JP"); // "アメリカ合衆国"
 * getDisplayName("EUR", "currency", "en-US"); // "Euro"
 */
export function getDisplayName(
  code: string,
  type: "language" | "region" | "currency" | "script",
  locale: string
): string {
  return new Intl.DisplayNames(locale, { type }).of(code) ?? code;
}
